import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Observable, of, timer} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import * as moment from 'moment';

import {Audit, AuditOwnerGroup, AuditType, SMEApprover} from '../../audit.model';
import {AuditService} from '../../audit.service';
import { Employee } from 'src/app/shared/shared.model';

@Component({
  selector: 'iaud-audit-edit-form',
  templateUrl: './audit-edit-form.component.html',
  styleUrls: ['./audit-edit-form.component.scss'],
})
export class AuditEditFormComponent implements OnInit {
  today: Date = moment().toDate();
  auditTypes$: Observable<Array<AuditType>>;
  auditOwnerGroups$: Observable<Array<AuditOwnerGroup>>;
  auditForm: FormGroup;

  smeApprovers1$: Observable<Array<SMEApprover>>;
  smeApprovers$: Observable<Array<SMEApprover>>;
  submiters$: Observable<Array<Employee>>;  
  canUpdate : boolean;
  submiter : string;
  submiterSelected: string;
  smeApprover : string;
  smeSelected: string;
  
  isDraft : boolean;  

  @Input() audit: Audit;
  @Input() isNew: boolean = false;
  @Output()
  isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  isFinishLaterValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private formBuilder: FormBuilder,
    private auditService: AuditService
  ) {}

  ngOnInit(): void {
    this.auditForm = this.generateForm();

    this.auditTypes$ = this.auditService.getAuditTypes()
      .pipe(
        map((types: Array<AuditType>) => types.filter(type => type.isActive)),
        tap(res => {
          const selectedType = res.find(type => type.auditType === this.audit.auditType);

          this.auditForm.get('type').setValue(selectedType || null);
        })
      );

    this.auditOwnerGroups$ = this.auditService.getAuditOwnerGroups()
      .pipe(
        map((groups: Array<AuditOwnerGroup>) => groups.filter(group => group.isActive)),
        tap(res => {
          const selectedGroup = res.find(group => group.id === this.audit.auditOwnerGroupId);
          this.auditForm.get('ownerGroup').setValue(selectedGroup || null);
        })
      );

    this.smeApprovers$ = this.auditForm.get('ownerGroup').valueChanges
      .pipe(
        distinctUntilChanged(),
        filter((ownerGroup: AuditOwnerGroup) => !!ownerGroup),
        switchMap((ownerGroup: AuditOwnerGroup) => this.auditService.getSmeApprovers(ownerGroup.id))
        /*,tap(res => {
          const selected = res.find(sme => sme.username === this.smeSelected);  
          alert(this.smeSelected);        
          this.auditForm.get('smeApprovers').setValue(selected || null);
        })
        */
      );            

    this.submiters$ = this.auditService.getEmployees("")
      .pipe(
        map((submiters: Array<Employee>) => submiters.filter(submiter => submiter.userId))
        /*,tap(res => {
          const selected = res.find(submiter => submiter.userId === this.submiter);
          //his.sidForm.get('submiters').setValue(selected || null);
        })
        */
      );

    this.auditForm.statusChanges
      .subscribe(() => {
        this.isValidChange.emit(this.auditForm.valid);
        this.isFinishLaterValidChange
          .emit(this.auditForm.get('title').valid
            && this.auditForm.get('ownerGroup').valid && this.auditForm.get('type').valid && this.auditForm.get('dueDate').valid);
      });

    this.auditForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(values => values.type && values.ownerGroup && values.type.id && values.ownerGroup.id)
      )
      .subscribe(values => {
        this.audit.auditTitle = values.title;
        this.audit.auditCode = values.code;
        this.audit.auditTypeId = values.type.id;
        this.audit.auditType = values.type.auditType;
        this.audit.auditDescription = values.description;
        this.audit.auditOwnerGroupId = values.ownerGroup.id;
        this.audit.auditOwnerGroup = values.ownerGroup.auditOwnerGroup;
        this.audit.dueDate = values.dueDate;
        this.audit.externalPartner = values.externalPartner;        

        this.audit.smeApprover = this.smeSelected;                   
        this.audit.submiter = this.submiterSelected;
        
        this.audit.canUpdate = values.canUpdate;    

      });

    if (this.audit.dueDate)
      this.auditForm.get('dueDate').markAsTouched();
      
  }

  onSubmiterSelection() {
    this.audit.submiter = this.submiterSelected;
  }

  onSmeSelection() {
    this.audit.smeApprover = this.smeSelected;                           
  }

  generateForm(): FormGroup {

    this.canUpdate = this.audit.canUpdate;   
    this.submiter =   this.audit.submiter;     
    this.submiterSelected = this.audit.submiter;                 
    this.smeApprover =   this.audit.smeApprover;     
    this.smeSelected = this.audit.smeApprover;
    
    this.isDraft = false;    

    if(this.audit.status.toLowerCase().trim() == "draft"){
      this.isDraft = true;
    }             

    return this.formBuilder.group({
      title: [this.audit.auditTitle, [
        Validators.required,
        Validators.maxLength(50),
      ], [this.isAuditNameAvailableValidator.bind(this)]],
      code: [this.audit.auditCode, Validators.maxLength(50)],
      type: [this.audit.auditType, Validators.required],
      description: [this.audit.auditDescription, [Validators.required, Validators.maxLength(2000)]],
      dueDate: [this.audit.dueDate, Validators.required],
      ownerGroup: [this.audit.auditOwnerGroup, Validators.required],
      smeApprovers: [this.audit.smeApprover, Validators.required],
      externalPartner: [this.audit.externalPartner, Validators.maxLength(250)],
      submiters:  [this.audit.submiter],
    });
  }

  auditTypeCompareFn(type1: AuditType, type2: AuditType): boolean {
    return type1 && type2 ? type1.auditType === type2.auditType : type1 === type2;
  }

  auditOwnerGroupCompareFn(group1: AuditOwnerGroup, group2: AuditOwnerGroup): boolean {
    return group1 && group2 ? group1.id === group2.id : group1 === group2;
  }

  auditSMECompareFn(sme1: Employee, sme2: Employee): boolean {
    return sme1 && sme2 ? sme1.userId === sme2.userId : sme1 === sme2;
  }

  isAuditNameAvailableValidator(control: AbstractControl): Observable<ValidationErrors> {
    return timer(500)
      .pipe(
        switchMap(() => {
          if (!this.isNew)
            return of(null);

          return this.auditService.checkAuditExistence(control.value)
            .pipe(
              distinctUntilChanged(),
              map((exists: boolean) => exists ? {exists: true} : null)
            );
        })
      );
  }
}
